import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Language, t as translate, languageLabels } from './translations';

interface I18nContextType {
    language: Language;
    setLanguage: (lang: Language) => void;
    t: (key: string) => string;
    languageLabels: Record<Language, string>;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

interface I18nProviderProps {
    children: ReactNode;
}

export const I18nProvider: React.FC<I18nProviderProps> = ({ children }) => {
    const [language, setLanguageState] = useState<Language>(() => {
        // Check localStorage or browser language
        const saved = localStorage.getItem('hiva_language') as Language;
        if (saved && ['pt', 'en', 'fr'].includes(saved)) {
            return saved;
        }
        // Default based on browser language
        const browserLang = navigator.language.split('-')[0].toLowerCase();
        if (browserLang === 'pt') return 'pt';
        if (browserLang === 'fr') return 'fr';
        return 'en';
    });

    const setLanguage = (lang: Language) => {
        setLanguageState(lang);
        localStorage.setItem('hiva_language', lang);
    };

    const t = (key: string) => translate(key, language);

    useEffect(() => {
        document.documentElement.lang = language;
    }, [language]);

    return (
        <I18nContext.Provider value={{ language, setLanguage, t, languageLabels }}>
            {children}
        </I18nContext.Provider>
    );
};

export const useI18n = (): I18nContextType => {
    const context = useContext(I18nContext);
    if (!context) {
        throw new Error('useI18n must be used within an I18nProvider');
    }
    return context;
};
